/**
 * Carga masiva de comisiones TPV (feature 156) — entidades del dominio.
 *
 * TypeScript PURO: cero imports de Angular. El contrato del back
 * (`CONTRATO_FRONT_CARGA_MASIVA_TASAS_TPV.md`, entregado al humano) carga la
 * comisión de UN nivel —Tasa Empresa, Tasa Unidad de negocio o Tasa Comercio—
 * en sus cuatro tipos de tarjeta, a partir de un Excel que el front lee y manda
 * como filas JSON.
 *
 * ⚠️ Las tasas deciden cuánto se le cobra a un comercio: ningún valor capturado
 * se redondea, recorta ni convierte en el front (RN-03/RN-05). Por eso una celda
 * es `TasaCellValue` tal cual la entregó el lector, y no un `number`.
 */

export type TasaBulkLevel = 'company' | 'unit' | 'merchant';

/** Orden canónico de las pestañas (Empresas · Unidades de negocio · Comercios). */
export const TASA_BULK_LEVELS: readonly TasaBulkLevel[] = ['company', 'unit', 'merchant'];

export type TasaCardType = 'credito' | 'debito' | 'internacional' | 'amex';

/** Orden de las cuatro columnas de comisión (§Columnas del contrato). */
export const TASA_CARD_TYPES: readonly TasaCardType[] = ['credito', 'debito', 'internacional', 'amex'];

export type TasaNameKey = 'empresa' | 'unidad' | 'comercio';

/** Plazos de MSI de la plantilla de Empresas (enmienda 2026-10-21, R60). */
export type TasaMsiTerm = 3 | 6 | 9 | 12 | 18 | 36;
export const TASA_MSI_TERMS: readonly TasaMsiTerm[] = [3, 6, 9, 12, 18, 36];

/** Las tres columnas de cada plazo, en el orden de la plantilla (Agregador · Banco · Mínimo). */
export type TasaMsiPart = 'agregador' | 'banco' | 'minimo';
export const TASA_MSI_PARTS: readonly TasaMsiPart[] = ['agregador', 'banco', 'minimo'];

export type TasaMsiKey = `msi${TasaMsiTerm}_${TasaMsiPart}`;

/**
 * Columnas de Empresas que NO son comisión ni nombre (R60): los montos de transacción y los
 * plazos de MSI. Solo existen en Empresas; en Unidades y Comercios siguen siendo ajenas.
 */
export type TasaCompanyExtraKey = 'minimo' | 'maximo' | TasaMsiKey;

/** Llave de un plazo y una de sus tres columnas. */
export function tasaMsiKey(term: TasaMsiTerm, part: TasaMsiPart): TasaMsiKey {
  return `msi${term}_${part}`;
}

/** Las llaves extra de Empresas en el orden de la plantilla del back. */
export const TASA_COMPANY_EXTRA_KEYS: readonly TasaCompanyExtraKey[] = [
  'minimo',
  'maximo',
  ...TASA_MSI_TERMS.flatMap((term) => TASA_MSI_PARTS.map((part) => tasaMsiKey(term, part))),
];

/** Qué es cada llave de MSI (plazo y columna), para la compuerta y los motivos. */
export const TASA_MSI_KEY_INFO: ReadonlyMap<TasaCompanyExtraKey, { term: TasaMsiTerm; part: TasaMsiPart }> = new Map(
  TASA_MSI_TERMS.flatMap((term) =>
    TASA_MSI_PARTS.map((part) => [tasaMsiKey(term, part), { term, part }] as const)),
);

/** Columnas de nombre que identifican un registro en cada nivel (§Columnas, jerárquicas). */
export const TASA_NAME_KEYS: Readonly<Record<TasaBulkLevel, readonly TasaNameKey[]>> = {
  company: ['empresa'],
  unit: ['empresa', 'unidad'],
  merchant: ['empresa', 'unidad', 'comercio'],
};

/**
 * Segmento de la ruta del back (`/api/v1/tpv/<segmento>/tasa/bulk/`) y valor del
 * `?nivel=` de la pantalla: son el mismo vocabulario a propósito, así una URL de
 * la pantalla se lee igual que el endpoint que consulta.
 */
export const TASA_BULK_LEVEL_SLUG: Readonly<Record<TasaBulkLevel, string>> = {
  company: 'companies',
  unit: 'business-units',
  merchant: 'merchants',
};

/** `nivel` que el back declara en la lista (§1). */
export const TASA_BULK_BACK_LEVEL: Readonly<Record<TasaBulkLevel, string>> = {
  company: 'empresa',
  unit: 'unidad',
  merchant: 'comercio',
};

/** Tope del contrato: de 1 a 500 ids en la plantilla y hasta 500 filas en la carga. */
export const TASA_BULK_MAX = 500;

export interface TasaBulkCandidate {
  readonly id: string;
  readonly companyName: string | null;
  /** `null` en Empresas. */
  readonly unitName: string | null;
  /** `null` fuera de Comercios. */
  readonly merchantName: string | null;
  /** SOLO `cargable === true` del back; cualquier otra cosa no se puede elegir. */
  readonly selectable: boolean;
  /** Motivo traducible de por qué no se puede elegir; `null` si es seleccionable. */
  readonly unavailableReason: readonly TasaReasonPart[] | null;
}

/**
 * Motivo de no carga (o de no disponible), por CÓDIGO del catálogo cerrado de
 * §Motivos. El texto del back nunca se conserva: se traduce por código.
 */
export type TasaReasonCode =
  // valor
  | 'moreThanTwoDecimals' | 'greaterThan100' | 'lessThan0' | 'notANumber'
  // valor, SOLO del front (compuerta del 2026-10-21): textos más claros para corregir
  | 'negativeNotAllowed' | 'onlyDigitsAndDot' | 'hasSpaces'
  // entre niveles
  | 'companyWithoutTasa' | 'unitWithoutTasa' | 'lowerThanCompany' | 'lowerThanUnit'
  | 'higherThanAUnit' | 'higherThanAMerchant' | 'unitOrMerchantOutOfRange' | 'merchantOutOfRange'
  // registro
  | 'companyNotFound' | 'unitNotFound' | 'merchantNotFound'
  | 'companyNameRepeated' | 'unitNameRepeated' | 'merchantNameRepeated'
  | 'companyRepeatedInFile' | 'unitRepeatedInFile' | 'merchantRepeatedInFile'
  | 'missingCompany' | 'missingUnit' | 'missingMerchant'
  // valor de los montos y del mínimo de MSI (Empresas, enmienda 2026-10-21)
  | 'lessThan1' | 'greaterThanMaxAmount' | 'mustBeInteger' | 'lessThan0Amount'
  // fila
  | 'noCommissions' | 'foreignColumns' | 'internalRowError'
  // fila de Empresas (enmienda 2026-10-21): «El mínimo debe ser menor al máximo» y
  // «MSI N meses: captura agregador, banco y mínimo juntos» (con `term`)
  | 'minNotLessThanMax' | 'msiIncomplete'
  // front: parte que no está en el catálogo
  | 'unknown';

export interface TasaReasonPart {
  /** Tipo de tarjeta del prefijo («Crédito: …»), o `null` si el motivo es de la fila. */
  readonly card: TasaCardType | null;
  readonly code: TasaReasonCode;
  /** Solo `foreignColumns`: nombres que coinciden con encabezados del archivo enviado. */
  readonly columns?: readonly string[];
  /**
   * Columna de Empresas del prefijo («Monto de transacción mínimo: …», «MSI 3 meses · Banco: …»),
   * si el motivo es de una de ellas (R65). Excluyente con `card`.
   */
  readonly field?: TasaCompanyExtraKey;
  /** Solo `msiIncomplete`: el plazo que vino a medias. */
  readonly term?: TasaMsiTerm;
}

export interface TasaBulkCandidateList {
  readonly level: TasaBulkLevel;
  readonly items: readonly TasaBulkCandidate[];
}

/** Celda tal cual: texto, número, booleano o vacío. Nunca se redondea ni se convierte. */
export type TasaCellValue = string | number | boolean | null;

/** Una fila tal como viaja en `{filas}` (solo las llaves del nivel). */
export type TasaBulkRow = Readonly<Partial<Record<TasaNameKey | TasaCardType | TasaCompanyExtraKey, TasaCellValue>>>;

export type TasaRowStatus = 'loaded' | 'notLoaded';

export interface TasaBulkRowResult {
  /** Posición 1-based sobre las `filas` enviadas. */
  readonly fila: number;
  readonly status: TasaRowStatus;
  /** Vacío si se cargó. */
  readonly reasons: readonly TasaReasonPart[];
}

export type TasaBulkOutcome = 'empty' | 'all' | 'some' | 'none';

export interface TasaBulkUploadResult {
  readonly total: number;
  readonly loaded: number;
  readonly notLoaded: number;
  readonly outcome: TasaBulkOutcome;
  readonly rows: readonly TasaBulkRowResult[];
}

export interface TasaBulkTemplateFile {
  readonly fileName: string;
  readonly fileBase64: string;
}
